import React from "react";
import PropTypes from "prop-types";

import { DataGrid } from "@mui/x-data-grid";
import CustomNoRowsOverlay from "app/components/overlays/CustomNoRowsOverlay";
import LinearProgress from '@mui/material/LinearProgress';
import MDBox from "components/MDBox";
import { isEmpty } from "utils/validationUtil";
import { getDateInDDMMYYYYFormat, getMonthName } from "utils/dateUtil";
import { compareText } from "utils/optionSort";
import { FilterAndExportGridToolbar } from "../overlays/FilterAndExportGridToolbar";

// Row key must include clientId: the same person can appear once per client (Admin all-clients),
// and those rows share year/month/memberId — without clientId the grid would drop one.
// @since NEXT_VERSION
export const buildRowId = (row) =>
  `${row.isMember === false ? "MGR" : "MBR"}-${row.clientId}-${row.year}${row.month}${row.memberId}`;

// The label the Client column shows, so the client ordering follows what the reader sees
// on screen rather than the raw client id behind it.
// @since NEXT_VERSION
const clientLabelOf = (clientLabelById, row) =>
  clientLabelById[String(row.clientId)] ?? String(row.clientId);

// Default order: Month Start (its year/month, already on the row), then Client Name, then
// Member Name. On the rows, not a sortModel: community DataGrid keeps only its first item.
// @since NEXT_VERSION
const sortActivityStatsRows = (rows, clientLabelById = {}) =>
  (Array.isArray(rows) ? [...rows] : []).sort(
    (a, b) =>
      a.year - b.year ||
      a.month - b.month ||
      compareText(clientLabelOf(clientLabelById, a), clientLabelOf(clientLabelById, b)) ||
      compareText(a.memberName, b.memberName)
  );

const buildColumns = (clientLabelById) => [
    { field: 'memberName', headerName: 'Member Name', flex: 2, minWidth: 240 },
    { field: 'clientId', headerName: 'Client', flex: 1, minWidth: 160, valueGetter: (value, row) => clientLabelOf(clientLabelById, row) },
    { field: 'firstDayOfMonthDate', headerName: 'Month Start', flex: 1, minWidth: 160, valueGetter: (value, row) => getDateInDDMMYYYYFormat(row.firstDayOfMonthDate) },
    { field: 'lastDayOfMonthDate', headerName: 'Month End', flex: 1, minWidth: 160, valueGetter: (value, row) => getDateInDDMMYYYYFormat(row.lastDayOfMonthDate) },
    { field: 'year', headerName: 'Month', flex: 1, minWidth: 120, valueGetter: (value, row) => getMonthName(row.month).substring(0,3) +" "+ row.year },
    { field: 'monthlyHoursSpent', headerName: 'Billed Hours', flex: 1, minWidth: 180},
]

export const ActivityStatsDataGridBase = (props) => {
    const [pageSize, setPageSize] = React.useState(10);
    const columns = React.useMemo(() => buildColumns(props.clientLabelById ?? {}), [props.clientLabelById]);

    const rows = React.useMemo(() => {
        if(isEmpty(props.activityStatsData) || props.activityStatsData.length === 0)
            return [];
        else
            return sortActivityStatsRows(props.activityStatsData, props.clientLabelById ?? {});
    }, [props.activityStatsData, props.clientLabelById]);

    return (
        <MDBox pt={3}
            py={3}
            px={2}
            height={500}
        >
            <DataGrid
                density="compact"
                rows={ rows }
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 20]}
                components={{
                    Toolbar: FilterAndExportGridToolbar,
                    LoadingOverlay: LinearProgress,
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
                getRowId={buildRowId}
            />
        </MDBox>
    );
}

ActivityStatsDataGridBase.propTypes = {
    activityStatsData: PropTypes.array.isRequired,
    clientLabelById: PropTypes.object,
}
